import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
  DestroyRef,
  effect,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, of, switchMap, tap, debounceTime, distinctUntilChanged, Observable } from 'rxjs';
import { Avatar } from '../../../shared/components/avatar/avatar';
import { AuthService } from '../../../services/auth';
import {
  PageTitleService,
  ToastService,
  ApiErrorService,
} from '../../../shared/services';
import { BusinessService, BusinessTypeService } from '../../../services/business';
import { CountrySelectComponent } from '../../../ui/country-select/country-select';
import { CountryService } from '../../../services';
import { Country } from '../../../shared/models';
import { NgxMaskDirective } from 'ngx-mask';
import {
  UsersService,
  UserProfileDto,
  UpdateUserProfileRequest,
} from '../../../services/users/users.service';
import { BusinessType } from '../../../shared/models/business';

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Avatar,
    CountrySelectComponent,
    NgxMaskDirective,
  ],
  templateUrl: './profile.html',
  styleUrl: './profile.css',
})
export class Profile implements OnInit {
  private authService = inject(AuthService);
  private titleService = inject(PageTitleService);
  private toastService = inject(ToastService);
  private apiErrorService = inject(ApiErrorService);
  private countryService = inject(CountryService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private usersService = inject(UsersService);
  private businessService = inject(BusinessService);
  private businessTypeService = inject(BusinessTypeService);
  private cdr = inject(ChangeDetectorRef);

  loggedUser = computed(() => this.authService.getUserInfo());
  isImpersonating = computed(() => this.authService.isImpersonating());
  profileForm: FormGroup;
  isLoading = signal(false);
  isSaving = signal(false);
  profileData = signal<UserProfileDto | null>(null);
  isGoogleLinked = signal(false);
  businessLogoPreview = signal<string | null>(null);
  private businessLogoFile: File | null = null;
  private businessId = signal<string | null>(null);

  private selectedCountryId = signal<string | null>(null);
  private countriesSignal = this.countryService.getStore().getModel();
  businessTypes = this.businessTypeService.getStore().getModel();
  selectedCountry = computed<Country | null>(() => {
    const id = this.selectedCountryId();
    if (!id) return null;
    return this.countriesSignal().find((c) => c.countryId === id) ?? null;
  });
  phoneMask = computed(() => this.selectedCountry()?.phoneMask || '');
  phonePlaceholder = computed(
    () => this.selectedCountry()?.phoneHolder || 'Ej: 300 123 4567'
  );

  private static readonly RESERVED_SLUGS = new Set([
    'auth', 'signin', 'signup', 'login', 'register', 'logout',
    'dashboard', 'agendas', 'people', 'professionals', 'services',
    'settings', 'reports', 'inventory', 'sales', 'promos', 'bundles',
    'schedules', 'business-hours', 'user', 'system-admin',
    'membership-expired', 'booking', 'book',
    'privacy-policy', 'terms-of-service', 'privacy',
    'api', 'admin', 'app', 'www', 'help', 'support',
    'about', 'contact', 'blog', 'pricing', 'features',
  ]);

  private baseHost = window.location.origin;
  slugPrefix = window.location.host + '/';

  showPassword = signal(false);

  readonly descriptionMaxLength = 1000;
  private static readonly MAX_LOGO_BYTES = 5 * 1024 * 1024;

  /** Curated starting points; the custom picker stays available next to them. */
  readonly colorPresets = [
    '#465fff', '#0f766e', '#be185d', '#9333ea',
    '#c2410c', '#1e293b', '#b45309', '#0369a1',
  ];

  /** Form controls grouped by the section the user sees them in. */
  private static readonly SECTION_CONTROLS: Record<string, string[]> = {
    'Tu cuenta': ['fullName', 'countryId', 'phone'],
    'Tu negocio': ['businessName', 'businessTypeId', 'businessDescription'],
    'Contacto': ['address', 'businessPhone', 'businessWhatsApp', 'whatsAppSameAsPhone'],
    'Enlace y colores': ['publicSlug', 'primaryColor', 'secondaryColor'],
    'Contraseña': ['password'],
  };

  // Slug validation state
  slugValidationState = signal<{
    isValidating: boolean;
    isValid: boolean | null;
    isAvailable: boolean | null;
    message: string | null;
  }>({
    isValidating: false,
    isValid: null,
    isAvailable: null,
    message: null,
  });

  constructor() {
    this.profileForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      firstName: [''],
      lastName: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      countryId: ['', Validators.required],
      businessTypeId: [''],
      businessName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      businessDescription: ['', [Validators.maxLength(1000)]],
      publicSlug: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
        // Permite escribir guiones libremente durante la edición; se valida formato final en onSubmit
        Validators.pattern(/^[a-z0-9-]+$/)
      ]],
      primaryColor: ['#3b82f6', [Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],
      secondaryColor: ['#8b5cf6', [Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],
      address: [''],
      businessPhone: [''],
      businessWhatsApp: [''],
      whatsAppSameAsPhone: [false],
      password: [''],
    });

    this.profileForm
      .get('countryId')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id: string) => this.selectedCountryId.set(id || null));

    // WhatsApp same as phone toggle
    this.profileForm
      .get('whatsAppSameAsPhone')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((same: boolean) => {
        const whatsAppCtrl = this.profileForm.get('businessWhatsApp')!;
        if (same) {
          whatsAppCtrl.setValue(this.profileForm.get('businessPhone')?.value || '', { emitEvent: false });
          whatsAppCtrl.disable({ emitEvent: false });
        } else {
          whatsAppCtrl.enable({ emitEvent: false });
        }
      });

    // Sync whatsApp when phone changes and toggle is on
    this.profileForm
      .get('businessPhone')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((phone: string) => {
        if (this.profileForm.get('whatsAppSameAsPhone')?.value) {
          this.profileForm.get('businessWhatsApp')?.setValue(phone, { emitEvent: false });
        }
      });

    // Real-time slug validation
    this.profileForm
      .get('publicSlug')!
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((slug: string) => {
        const sanitized = this.normalizeSlugForInput(slug);
        if (slug !== sanitized) {
          this.profileForm.get('publicSlug')?.setValue(sanitized, {
            emitEvent: false,
          });
        }

        // Normalización estricta solo para validar / consultar disponibilidad
        const normalized = this.normalizePublicSlug(sanitized);

        if (!normalized || normalized.length < 3) {
          this.slugValidationState.set({
            isValidating: false,
            isValid: null,
            isAvailable: null,
            message: null,
          });
          return;
        }

        // Only validate if format is correct
        const formatValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized);
        if (!formatValid) {
          this.slugValidationState.set({
            isValidating: false,
            isValid: false,
            isAvailable: false,
            message: 'El slug debe terminar en letra o número',
          });
          return;
        }

        // Check reserved words
        if (Profile.RESERVED_SLUGS.has(normalized)) {
          this.slugValidationState.set({
            isValidating: false,
            isValid: false,
            isAvailable: false,
            message: 'Este nombre está reservado y no puede usarse',
          });
          return;
        }

        this.validateSlugRealTime(normalized);
      });

    effect(() => {
      const hasTypes = this.businessTypes().length > 0;
      const control = this.profileForm.get('businessTypeId');
      if (!control) return;
      if (!hasTypes && control.enabled) {
        control.disable({ emitEvent: false });
      } else if (hasTypes && control.disabled) {
        control.enable({ emitEvent: false });
      }
    });

  }

  ngOnInit(): void {
    this.titleService.setPage({
      title: this.isImpersonating() ? 'Perfil del negocio' : 'Tu perfil',
      subtitle: this.isImpersonating() ? 'Los datos de este negocio' : 'Tu nombre, foto y datos de contacto',
    });
    this.businessTypeService.fetchAndSetEntitiesToStore();
    this.countryService.fetchAndSetEntitiesToStore();

    // When impersonating, personal fields are hidden - remove their validators
    if (this.isImpersonating()) {
      this.profileForm.get('fullName')?.clearValidators();
      this.profileForm.get('countryId')?.clearValidators();
      this.profileForm.get('fullName')?.updateValueAndValidity({ emitEvent: false });
      this.profileForm.get('countryId')?.updateValueAndValidity({ emitEvent: false });
    }

    this.loadProfile();
  }

  get fullNameControl(): FormControl {
    return this.profileForm.get('fullName') as FormControl;
  }

  get emailControl(): FormControl {
    return this.profileForm.get('email') as FormControl;
  }

  get countryControl(): FormControl {
    return this.profileForm.get('countryId') as FormControl;
  }

  get passwordControl(): FormControl {
    return this.profileForm.get('password') as FormControl;
  }

  get businessNameControl(): FormControl {
    return this.profileForm.get('businessName') as FormControl;
  }

  get publicSlugControl(): FormControl {
    return this.profileForm.get('publicSlug') as FormControl;
  }

  get primaryColorControl(): FormControl {
    return this.profileForm.get('primaryColor') as FormControl;
  }

  get secondaryColorControl(): FormControl {
    return this.profileForm.get('secondaryColor') as FormControl;
  }

  onReset(): void {
    const data = this.profileData();
    if (data) {
      this.patchFormFromProfile(data);
    } else {
      this.patchFormFromToken();
    }
    this.profileForm.markAsPristine();
  }

  onSubmit(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.toastService.show('Revisa los campos marcados en rojo antes de guardar.', 'warning');
      this.focusFirstInvalidField();
      return;
    }
    if (this.isSlugChanged() && this.slugValidationState().isValidating) {
      this.toastService.show('Espera a que terminemos de revisar tu enlace.', 'warning');
      return;
    }
    if (this.isSlugChanged() && this.slugValidationState().isAvailable === false) {
      this.publicSlugControl.markAsTouched();
      this.toastService.show(
        this.slugValidationState().message || 'Ese enlace no está disponible. Prueba con otro.',
        'warning'
      );
      this.focusFirstInvalidField('publicSlug');
      return;
    }
    const rawValues = this.profileForm.getRawValue();
    const {
      fullName,
      email,
      phone,
      countryId,
      businessTypeId,
      businessName,
      businessDescription,
      publicSlug,
      primaryColor,
      secondaryColor,
      address,
      businessPhone,
      businessWhatsApp,
      whatsAppSameAsPhone,
      password,
    } = rawValues;

    // Split fullName into firstName/lastName for backend
    const nameParts = (fullName || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : firstName;

    const trimmedBusinessName = businessName?.trim() ?? '';
    const trimmedPublicSlug = this.normalizePublicSlug(publicSlug ?? '');
    const businessId = this.businessId() ?? this.loggedUser()?.business_id ?? null;
    const pendingLogoFile = this.businessLogoFile;
    const pendingLogoPreview =
      pendingLogoFile && this.businessLogoPreview()
        ? this.businessLogoPreview()
        : null;
    const businessNameChanged =
      !!trimmedBusinessName &&
      trimmedBusinessName !== this.getCurrentBusinessName();
    const businessDescriptionChanged = businessDescription !== (this.profileData()?.businessDescription ?? '');
    const publicSlugChanged =
      !!trimmedPublicSlug &&
      trimmedPublicSlug !== (this.profileData()?.publicSlug ?? '');
    const primaryColorChanged = primaryColor !== (this.profileData()?.primaryColor ?? '#3b82f6');
    const secondaryColorChanged = secondaryColor !== (this.profileData()?.secondaryColor ?? '#8b5cf6');
    const addressChanged = address !== (this.profileData()?.address ?? '');
    const businessPhoneChanged = businessPhone !== (this.profileData()?.businessPhone ?? '');
    const businessWhatsAppChanged = (whatsAppSameAsPhone ? businessPhone : businessWhatsApp) !== (this.profileData()?.businessWhatsApp ?? '');
    const colorsChanged = primaryColorChanged || secondaryColorChanged;

    const shouldUpdateBusiness =
      !!businessId && (businessNameChanged || businessDescriptionChanged || !!pendingLogoFile || colorsChanged || addressChanged || businessPhoneChanged || businessWhatsAppChanged);
    const shouldUpdateSlug =
      !!businessId && publicSlugChanged && trimmedPublicSlug.length >= 3;

    // Validación final del slug antes de enviar
    const finalSlugIsValid =
      !trimmedPublicSlug ||
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmedPublicSlug);

    if (!finalSlugIsValid) {
      this.publicSlugControl.setErrors({ pattern: true });
      this.publicSlugControl.markAsTouched();
      this.toastService.show(
        'El slug solo puede tener letras, números y guiones, y debe terminar en letra o número.',
        'warning'
      );
      return;
    }

    // Actualiza el control con la versión final normalizada para que sea la que se guarde
    this.publicSlugControl.setValue(trimmedPublicSlug, { emitEvent: false });

    const payload: UpdateUserProfileRequest = {
      firstName,
      lastName,
      email,
      phone,
      countryId,
      businessTypeId: businessTypeId || undefined,
      password:
        !this.isGoogleLinked() && password ? password : undefined,
    };

    this.isSaving.set(true);
    this.usersService
      .updateProfile(payload)
      .pipe(
        switchMap((response) => {
          this.profileData.set(response);
          this.isGoogleLinked.set(response.isGoogleLinked);
          this.patchFormFromProfile(response);
          if (pendingLogoFile) {
            this.businessLogoFile = pendingLogoFile;
            if (pendingLogoPreview) {
              this.businessLogoPreview.set(pendingLogoPreview);
            }
          }
          if (shouldUpdateBusiness) {
            if (trimmedBusinessName) {
              this.businessNameControl.setValue(trimmedBusinessName, {
                emitEvent: false,
              });
            }
            // Update description in form if it changed
            if (businessDescriptionChanged) {
              this.profileForm
                .get('businessDescription')
                ?.setValue(businessDescription || '', {
                  emitEvent: false,
                });
            }
          }

          // Build the update observables depending on what changed
          const tasks: Array<Observable<unknown>> = [];

          if (shouldUpdateBusiness && businessId) {
            const finalWhatsApp = whatsAppSameAsPhone ? businessPhone : businessWhatsApp;
            const businessUpdate$ = this.businessService
              .updateBusinessProfile(businessId, {
                name: trimmedBusinessName,
                description: businessDescription || undefined,
                logoFile: pendingLogoFile ?? undefined,
                primaryColor: primaryColor || undefined,
                secondaryColor: secondaryColor || undefined,
                address: address || undefined,
                phone: businessPhone || undefined,
                whatsApp: finalWhatsApp || undefined,
              })
              .pipe(
                tap((bizRes) => {
                  const updatedLogo =
                    bizRes?.LogoUrl ??
                    (bizRes as { logoUrl?: string })?.logoUrl ??
                    null;
                  this.profileData.update((current) => {
                    if (!current) return current;
                    return {
                      ...current,
                      businessName:
                        trimmedBusinessName || current.businessName || '',
                      businessDescription:
                        businessDescription ||
                        current.businessDescription ||
                        null,
                      businessLogoUrl:
                        updatedLogo ?? current.businessLogoUrl ?? null,
                      address: address || current.address || null,
                    };
                  });
                  if (updatedLogo) {
                    this.businessLogoPreview.set(updatedLogo);
                  }
                  if (businessNameChanged && trimmedBusinessName) {
                    this.authService.updateBusinessName(trimmedBusinessName);
                  }
                  this.businessLogoFile = null;
                })
              );
            tasks.push(businessUpdate$);
          }

          if (shouldUpdateSlug && businessId) {
            const slugUpdate$ = this.businessService
              .updateBusinessSlug(businessId, trimmedPublicSlug)
              .pipe(
                tap(() => {
                  this.profileData.update((current) => {
                    if (!current) return current;
                    return {
                      ...current,
                      publicSlug: trimmedPublicSlug,
                    };
                  });
                })
              );
            tasks.push(slugUpdate$);
          }

          if (!tasks.length) {
            this.profileForm.markAsPristine();
            return of(null);
          }

          return forkJoin(tasks).pipe(
            switchMap(() =>
              // Reload profile to get updated address, description and other business fields
              this.usersService.getProfile().pipe(
                tap((updatedProfile) => {
                  this.profileData.set(updatedProfile);
                  this.patchFormFromProfile(updatedProfile);
                  this.profileForm.markAsPristine();
                })
              )
            )
          );
        }),
        finalize(() => {
          this.isSaving.set(false);
        })
      )
      .subscribe({
        next: () => {
          let message = 'Perfil actualizado correctamente';
          if (shouldUpdateBusiness && shouldUpdateSlug) {
            message = 'Perfil, negocio y slug público actualizados correctamente';
          } else if (shouldUpdateBusiness) {
            message = 'Perfil y negocio actualizados correctamente';
          } else if (shouldUpdateSlug) {
            message = 'Perfil y slug público actualizados correctamente';
          }
          this.toastService.show(message, 'success');
        },
        error: (err) => {
          this.apiErrorService.handle(err);
        },
      });
  }

  private loadProfile(): void {
    this.isLoading.set(true);
    this.usersService
      .getProfile()
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: (profile) => {
          this.profileData.set(profile);
          this.isGoogleLinked.set(profile.isGoogleLinked);
          this.patchFormFromProfile(profile);
        },
        error: () => {
          this.profileData.set(null);
          // Only patch from token if profile load fails
          this.patchFormFromToken();
        },
      });
  }

  private patchFormFromProfile(profile: UserProfileDto): void {
    const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
    this.profileForm.patchValue({
      fullName: fullName,
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      email: profile.email ?? '',
      phone: profile.phone ?? '',
      countryId: profile.countryId ?? this.loggedUser()?.country_id ?? '',
      businessTypeId: profile.businessTypeId ?? '',
      businessName:
        profile.businessName ?? this.loggedUser()?.business_name ?? '',
      businessDescription: profile.businessDescription || '',
      publicSlug: profile.publicSlug ?? '',
      primaryColor: profile.primaryColor ?? '#3b82f6',
      secondaryColor: profile.secondaryColor ?? '#8b5cf6',
      address: profile.address || '',
      businessPhone: profile.businessPhone || '',
      businessWhatsApp: profile.businessWhatsApp || '',
      whatsAppSameAsPhone: profile.businessPhone && profile.businessWhatsApp && profile.businessPhone === profile.businessWhatsApp ? true : false,
      password: '',
    }, { emitEvent: false });
    // Disable whatsApp field if same as phone
    if (this.profileForm.get('whatsAppSameAsPhone')?.value) {
      this.profileForm.get('businessWhatsApp')?.disable({ emitEvent: false });
    }
    this.cdr.detectChanges();
    this.selectedCountryId.set(
      this.profileForm.get('countryId')?.value ?? null
    );
    this.businessId.set(
      profile.businessId ?? this.loggedUser()?.business_id ?? null
    );
    this.businessLogoPreview.set(profile.businessLogoUrl ?? null);
    this.businessLogoFile = null;
    this.showPassword.set(false);
    this.slugValidationState.set({
      isValidating: false,
      isValid: null,
      isAvailable: null,
      message: null,
    });
    if (this.isGoogleLinked()) {
      this.passwordControl.disable({ emitEvent: false });
    } else {
      this.passwordControl.enable({ emitEvent: false });
    }
    this.profileForm.markAsPristine();
  }

  private patchFormFromToken(): void {
    const info = this.loggedUser();
    if (!info) return;
    const fullName = [info.given_name, info.family_name].filter(Boolean).join(' ');
    this.profileForm.patchValue({
      fullName: fullName,
      firstName: info.given_name ?? '',
      lastName: info.family_name ?? '',
      email: info.email ?? '',
      phone: info.phone ?? '',
      countryId: info.country_id ?? '',
      businessTypeId: '',
      businessName: info.business_name ?? '',
      businessDescription: '',
      publicSlug: '',
      primaryColor: '#3b82f6',
      secondaryColor: '#8b5cf6',
      address: '',
      businessPhone: '',
      businessWhatsApp: '',
      whatsAppSameAsPhone: false,
      password: '',
    });
    this.selectedCountryId.set(info.country_id ?? null);
    this.businessId.set(info.business_id ?? null);
    this.businessLogoPreview.set(null);
    this.businessLogoFile = null;
    if (this.passwordControl.disabled && !this.isGoogleLinked()) {
      this.passwordControl.enable({ emitEvent: false });
    }
    this.profileForm.markAsPristine();
  }

  private getCurrentBusinessName(): string {
    return (
      this.profileData()?.businessName ??
      this.loggedUser()?.business_name ??
      ''
    );
  }

  private normalizePublicSlug(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Normalización amigable para el input:
   * - Convierte espacios en guiones al vuelo
   * - Permite guiones intermedios o finales mientras se escribe
   * - Limpia caracteres no permitidos
   */
  private normalizeSlugForInput(value: string): string {
    return (value ?? '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-{3,}/g, '--') // evita cadenas largas de guiones pero mantiene el guion recién escrito
      .trim();
  }

  onBusinessLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toastService.show('El logo debe ser una imagen (PNG, JPG o WEBP).', 'warning');
      return;
    }
    if (file.size > Profile.MAX_LOGO_BYTES) {
      this.toastService.show('El logo no puede pesar más de 5 MB.', 'warning');
      return;
    }
    this.businessLogoFile = file;
    const reader = new FileReader();
    reader.onload = () =>
      this.businessLogoPreview.set(reader.result as string);
    reader.readAsDataURL(file);
    this.profileForm.markAsDirty();
  }

  /** Discards a logo picked but not saved yet, going back to the saved one. */
  clearBusinessLogo(): void {
    this.businessLogoFile = null;
    const currentLogo = this.profileData()?.businessLogoUrl ?? null;
    this.businessLogoPreview.set(currentLogo);
  }

  get hasPendingLogo(): boolean {
    return this.businessLogoFile !== null;
  }

  private validateSlugRealTime(slug: string): void {
    const businessId = this.businessId();
    this.slugValidationState.set({
      isValidating: true,
      isValid: null,
      isAvailable: null,
      message: 'Validando...',
    });

    this.businessService.validateSlug(slug, businessId || undefined).subscribe({
      next: (response) => {
        this.slugValidationState.set({
          isValidating: false,
          isValid: response.isValid,
          isAvailable: response.isAvailable,
          message: response.message || null,
        });
      },
      error: () => {
        this.slugValidationState.set({
          isValidating: false,
          isValid: false,
          isAvailable: false,
          message: 'Error al validar el slug',
        });
      },
    });
  }

  /** Public URL for a slug; the saved slug is the one clients can open today. */
  slugUrl(slug: string | null = this.savedSlug()): string {
    return slug ? `${this.baseHost}/${slug}` : '';
  }

  savedSlug(): string | null {
    return this.profileData()?.publicSlug || null;
  }

  isSlugChanged(): boolean {
    const current = this.normalizePublicSlug(this.publicSlugControl.value ?? '');
    return current !== (this.savedSlug() ?? '');
  }

  copySlugUrl(): void {
    const url = this.slugUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => this.toastService.show('Enlace copiado', 'success'),
      () => this.toastService.show('No pudimos copiar el enlace. Cópialo manualmente: ' + url, 'warning')
    );
  }

  openMicrositePreview(): void {
    const url = this.slugUrl();
    if (!url) {
      this.toastService.show('Guarda un enlace para publicar tu micrositio.', 'warning');
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  setColor(control: 'primaryColor' | 'secondaryColor', hex: string): void {
    const ctrl = this.profileForm.get(control)!;
    if (ctrl.value?.toLowerCase() === hex.toLowerCase()) return;
    ctrl.setValue(hex);
    ctrl.markAsDirty();
  }

  /** Names of the sections that hold unsaved edits, for the save bar. */
  changedSections(): string[] {
    const sections = Object.entries(Profile.SECTION_CONTROLS)
      .filter(([, names]) => names.some((n) => this.profileForm.get(n)?.dirty))
      .map(([section]) => section);
    if (this.hasPendingLogo && !sections.includes('Tu negocio')) {
      sections.push('Tu negocio');
    }
    return sections;
  }

  hasChanges(): boolean {
    return this.profileForm.dirty || this.hasPendingLogo;
  }

  descriptionLength(): number {
    return (this.profileForm.get('businessDescription')?.value ?? '').length;
  }

  businessTypeName(): string | null {
    const id = this.profileForm.get('businessTypeId')?.value;
    if (!id) return null;
    return this.businessTypes().find((t: BusinessType) => t.businessTypeId === id)?.name ?? null;
  }

  mapsUrl(): string | null {
    const address = (this.profileForm.get('address')?.value ?? '').trim();
    if (!address) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  previewWhatsApp(): string {
    const raw = this.profileForm.getRawValue();
    return (raw.whatsAppSameAsPhone ? raw.businessPhone : raw.businessWhatsApp) || '';
  }

  businessInitials(): string {
    const name = (this.profileForm.get('businessName')?.value ?? '').trim();
    if (!name) return 'N';
    const words = name.split(/\s+/);
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
  }

  primaryColor(): string {
    return this.validHex(this.primaryColorControl.value, '#3b82f6');
  }

  secondaryColor(): string {
    return this.validHex(this.secondaryColorControl.value, '#8b5cf6');
  }

  /** Same banner the public microsite draws from the brand colors. */
  bannerGradient(): string {
    const p = this.primaryColor();
    const s = this.secondaryColor();
    return `linear-gradient(135deg, ${this.hexToRgba(p, 0.22)} 0%, ${this.hexToRgba(s, 0.14)} 55%, ${this.hexToRgba(p, 0.06)} 100%)`;
  }

  logoGradient(): string {
    return `linear-gradient(135deg, ${this.primaryColor()} 0%, ${this.secondaryColor()} 100%)`;
  }

  /** White or near-black, whichever reads better on the given background. */
  textOn(hex: string): string {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
      .map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.45 ? '#101828' : '#ffffff';
  }

  private validHex(value: string | null | undefined, fallback: string): string {
    return value && /^#[0-9A-Fa-f]{6}$/.test(value) ? value : fallback;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private focusFirstInvalidField(name?: string): void {
    setTimeout(() => {
      const selector = name
        ? `[formcontrolname="${name}"]`
        : 'input.ng-invalid, select.ng-invalid, textarea.ng-invalid, app-country-select.ng-invalid';
      const el = document.querySelector<HTMLElement>(`app-profile ${selector}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el.matches('input, select, textarea') ? el : el.querySelector<HTMLElement>('input, select, button'))?.focus({ preventScroll: true });
    });
  }
}
